<template>
  <v-card class="blue-grey lighten-5">
    <form-header
      :fullscreen="false"
      :form="form"
      tour="rate-calendar-bulk-creation-form"
      :title="$lang('rate-calendar.title_create')"
      with-close
      @cancel="cancel()"
    >
    </form-header>
    <v-card-text>
      <v-card outlined>
        <v-card-text>
          <v-container fluid>
            <v-row>
              <v-col md="2" xs="12">
                <v-date
                  dense
                  v-model="form.start_date"
                  :label="$lang('rate-calendar.fields.from')"
                  :error="!!validation.start_date"
                  :error-messages="!!validation.start_date ? validation.start_date : []"
                  :min="$moment().format('YYYY-MM-DD')"
                />
              </v-col>
              <v-col md="2" xs="12">
                <v-date
                  dense
                  v-model="form.end_date"
                  :min="form.start_date"
                  :label="$lang('rate-calendar.fields.to')"
                  :error="!!validation.end_date"
                  :error-messages="!!validation.end_date ? validation.end_date : []"
                />
              </v-col>
              <v-col align="center" md="4" xs="12">
                <v-btn-toggle multiple v-model="form.weekdays" background-color="white" color="teal" class="hidden-xs-only">
                  <v-btn :value="1" small>
                    {{ $lang('main.weekdays_short.mon') }}
                  </v-btn>
                  <v-btn :value="2" small>
                    {{ $lang('main.weekdays_short.tue') }}
                  </v-btn>
                  <v-btn :value="3" small>
                    {{ $lang('main.weekdays_short.wed') }}
                  </v-btn>
                  <v-btn :value="4" small>
                    {{ $lang('main.weekdays_short.thu') }}
                  </v-btn>
                  <v-btn :value="5" small>
                    {{ $lang('main.weekdays_short.fri') }}
                  </v-btn>
                  <v-btn :value="6" small>
                    {{ $lang('main.weekdays_short.sat') }}
                  </v-btn>
                  <v-btn :value="0" small>
                    {{ $lang('main.weekdays_short.sun') }}
                  </v-btn>
                </v-btn-toggle>
              </v-col>
              <v-col cols="12" xs="12" md="2">
                <v-boolean
                  class="mt-0"
                  v-model="form.skip_derived"
                  hide-details
                  dense
                  :false-value="0"
                  :label="$lang('rate-calendar.fields.skip_derived_tariff')">
                </v-boolean>
              </v-col>
            </v-row>
            <v-row>
              <v-col v-show="!initialized">
                <v-skeleton-loader
                  :tile="false"
                  :boilerplate="false"
                  type="table-row-divider@5"
                  class="mx-auto"
                ></v-skeleton-loader>
              </v-col>
              <v-col v-show="initialized">
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th colspan="2" class="text-center blue-grey lighten-4">
                          {{ $lang("rate-calendar.length_of_stay") }}
                        </th>
                        <th colspan="2" class="text-center blue-grey lighten-3">
                          {{ $lang("rate-calendar.restrictions") }}
                        </th>
                      </tr>
                      <tr>
                        <th class="text-left">
                          {{ $lang('rate-calendar.room_type_title') }}
                        </th>
                        <th class="text-left">
                          {{$lang('rate-calendar.fields.stop_sell')}}
                        </th>
                        <th class="text-left">
                          {{$lang('rate-calendar.fields.availability')}}
                        </th>
                        <th class="text-left">
                          {{$lang('rate-calendar.fields.price')}}
                        </th>
                        <th class="text-left">
                          {{$lang('rate-calendar.fields.min')}}
                        </th>
                        <th class="text-left">
                          {{$lang('rate-calendar.fields.max')}}
                        </th>
                        <th class="text-left">
                          {{$lang('rate-calendar.fields.cta')}}
                        </th>
                        <th class="text-left">
                          {{$lang('rate-calendar.fields.ctd')}}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr class="blue-grey lighten-5">
                        <td>
                          <v-icon>mdi-expand-all</v-icon>
                          {{ $lang('rate-calendar.apply_all') }}
                        </td>
                        <td>
                          <v-checkbox
                            v-model="form.stop_sell"
                            :true-value="1"
                            :false-value="0"
                            color="red"
                            hide-details
                            @change="applyAll('stop_sell')"
                            indeterminate
                            dense
                            on-icon="mdi-stop-circle"
                            off-icon="mdi-play-circle"
                          />
                        </td>
                        <td>
                          <div class="d-flex">
                            <v-btn-toggle v-model="form.auto_availability" color="teal" class="mr-1" background-color="transparent">
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                  <v-btn
                                    :value="$const('ConstBoolean', 'TRUE')"
                                    x-small
                                    v-on="on"
                                    class="my-2 px-0"
                                  >
                                    <v-icon x-small color="red">mdi-auto-fix</v-icon> AUTO
                                  </v-btn>
                                  <v-btn
                                    :value="$const('ConstBoolean', 'FALSE')"
                                    x-small
                                    class="my-2 px-0"
                                  >
                                    <v-icon x-small>mdi-equal</v-icon>
                                  </v-btn>
                                </template>
                                <span>{{ $lang('rate-calendar.fields.auto_availability') }}</span>
                              </v-tooltip>
                            </v-btn-toggle>
                            <v-text-field
                              v-model="form.availability"
                              dense
                              :placeholder="$lang('rate-calendar.fields.availability')"
                              :error="!!validation.availability"
                              :error-messages="!!validation.availability ? validation.availability : []"
                              hide-details
                              single-line
                              @change="applyAll('availability')"
                              :disabled="!!form.auto_availability"
                            />
                        </div>
                        </td>
                        <td>
                          <div class="d-flex">
                            <v-text-field
                              v-model="form.amount"
                              dense
                              :placeholder="$lang('rate-calendar.fields.price')"
                              :error="!!validation.amount"
                              :error-messages="!!validation.amount ? validation.amount : []"
                              hide-details
                              single-line
                              @change="applyAll('amount')"
                            ></v-text-field>
                            <v-menu offset-y>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  v-bind="attrs"
                                  v-on="on"
                                  class="ma-2 px-0"
                                  x-small
                                >
                                  <v-icon>mdi-menu-down</v-icon>
                                </v-btn>
                              </template>
                              <v-list>
                                <v-list-item>
                                  <v-list-item-title>
                                    <v-btn-toggle v-model="form.price_variation" mandatory background-color="white" color="teal" @change="applyAll('price_variation')">
                                      <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                          <v-btn
                                            :value="1"
                                            small
                                            style="min-width: initial;"
                                            v-bind="attrs"
                                            v-on="on"
                                          >
                                            <v-icon small>mdi-equal</v-icon>
                                          </v-btn>
                                        </template>
                                        <span>{{ $lang('rate-calendar.variation.exact') }}</span>
                                      </v-tooltip>
                                      <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                          <v-btn
                                            :value="2"
                                            small
                                            style="min-width: initial;"
                                            v-bind="attrs"
                                            v-on="on"
                                          >
                                            <v-icon small>mdi-arrow-up-down</v-icon>
                                          </v-btn>
                                        </template>
                                        <span>{{ $lang('rate-calendar.variation.value') }}</span>
                                      </v-tooltip>
                                      <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                          <v-btn
                                            :value="3"
                                            small
                                            style="min-width: initial;"
                                            v-bind="attrs"
                                            v-on="on"
                                          >
                                            <v-icon small>mdi-percent</v-icon>
                                          </v-btn>
                                        </template>
                                        <span>{{ $lang('rate-calendar.variation.percent') }}</span>
                                      </v-tooltip>
                                    </v-btn-toggle>
                                  </v-list-item-title>
                                </v-list-item>
                              </v-list>
                            </v-menu>
                          </div>
                        </td>
                        <td>
                          <v-text-field
                            v-model="form.min_stay"
                            dense
                            :placeholder="$lang('rate-calendar.fields.min')"
                            :error="!!validation.min_stay"
                            :error-messages="!!validation.min_stay ? validation.min_stay : []"
                            hide-details
                            single-line
                            @change="applyAll('min_stay')"
                          ></v-text-field>
                        </td>
                        <td>
                          <v-text-field
                            v-model="form.max_stay"
                            dense
                            :placeholder="$lang('rate-calendar.fields.max')"
                            :error="!!validation.max_stay"
                            :error-messages="!!validation.max_stay ? validation.max_stay : []"
                            hide-details
                            single-line
                            @change="applyAll('max_stay')"
                          ></v-text-field>
                        </td>
                        <td>
                          <v-checkbox
                            v-model="form.cta"
                            :true-value="1"
                            :false-value="0"
                            color="red"
                            hide-details
                            @change="applyAll('cta')"
                            indeterminate
                            dense
                            on-icon="mdi-login"
                            off-icon="mdi-login"
                          />
                        </td>
                        <td>
                          <v-checkbox
                            v-model="form.ctd"
                            :true-value="1"
                            :false-value="0"
                            color="red"
                            hide-details
                            @change="applyAll('ctd')"
                            indeterminate
                            dense
                            on-icon="mdi-logout"
                            off-icon="mdi-logout"
                          />
                        </td>
                      </tr>
                      <tr
                        v-for="roomType in roomTypes"
                        :key="roomType.id"
                      >
                        <td class="caption">
                          {{roomType.name}}
                          <span v-if="!filters.withRoomTypeNames">
                            / <v-icon small>mdi-human-male</v-icon>x{{roomType.guests}}
                          </span>
                        </td>
                        <td>
                          <v-checkbox
                            v-model="roomType.stop_sell"
                            :true-value="1"
                            :false-value="0"
                            color="red"
                            hide-details
                            indeterminate
                            dense
                            on-icon="mdi-stop-circle"
                            off-icon="mdi-play-circle"
                          />
                        </td>
                        <td>
                          <div class="d-flex">
                            <v-btn-toggle v-model="roomType.auto_availability" color="teal" class="mr-1" background-color="transparent">
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                  <v-btn
                                    :value="$const('ConstBoolean', 'TRUE')"
                                    x-small
                                    v-on="on"
                                    class="my-2 px-0"
                                  >
                                    <v-icon x-small color="red">mdi-auto-fix</v-icon> AUTO
                                  </v-btn>
                                  <v-btn
                                    :value="$const('ConstBoolean', 'FALSE')"
                                    x-small
                                    class="my-2 px-0"
                                  >
                                    <v-icon x-small>mdi-equal</v-icon>
                                  </v-btn>
                                </template>
                                <span>{{ $lang('rate-calendar.fields.auto_availability') }}</span>
                              </v-tooltip>
                            </v-btn-toggle>
                            <v-text-field
                              v-model="roomType.availability"
                              dense
                              :placeholder="$lang('rate-calendar.fields.availability')"
                              :error="!!validation.availability"
                              :error-messages="!!validation.availability ? validation.availability : []"
                              hide-details
                              single-line
                              :disabled="!!roomType.auto_availability"
                            />
                          </div>
                        </td>
                        <td>
                          <div class="d-flex">
                            <v-text-field
                              v-model="roomType.amount"
                              dense
                              :placeholder="$lang('rate-calendar.fields.price')"
                              :error="!!validation.amount"
                              :error-messages="!!validation.amount ? validation.amount : []"
                              hide-details
                              single-line
                            ></v-text-field>
                            <v-menu offset-y>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  v-bind="attrs"
                                  v-on="on"
                                  class="ma-2 px-0"
                                  x-small
                                >
                                  <v-icon>mdi-menu-down</v-icon>
                                </v-btn>
                              </template>
                              <v-list>
                                <v-list-item>
                                  <v-list-item-title>
                                    <v-btn-toggle v-model="roomType.price_variation" mandatory background-color="white" color="teal">
                                      <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                          <v-btn
                                            :value="1"
                                            small
                                            style="min-width: initial;"
                                            v-bind="attrs"
                                            v-on="on"
                                          >
                                            <v-icon small>mdi-equal</v-icon>
                                          </v-btn>
                                        </template>
                                        <span>{{ $lang('rate-calendar.variation.exact') }}</span>
                                      </v-tooltip>
                                      <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                          <v-btn
                                            :value="2"
                                            small
                                            style="min-width: initial;"
                                            v-bind="attrs"
                                            v-on="on"
                                          >
                                            <v-icon small>mdi-arrow-up-down</v-icon>
                                          </v-btn>
                                        </template>
                                        <span>{{ $lang('rate-calendar.variation.value') }}</span>
                                      </v-tooltip>
                                      <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                          <v-btn
                                            :value="3"
                                            small
                                            style="min-width: initial;"
                                            v-bind="attrs"
                                            v-on="on"
                                          >
                                            <v-icon small>mdi-percent</v-icon>
                                          </v-btn>
                                        </template>
                                        <span>{{ $lang('rate-calendar.variation.percent') }}</span>
                                      </v-tooltip>
                                    </v-btn-toggle>
                                  </v-list-item-title>
                                </v-list-item>
                              </v-list>
                            </v-menu>
                          </div>
                        </td>
                        <td>
                          <v-text-field
                            v-model="roomType.min_stay"
                            dense
                            :placeholder="$lang('rate-calendar.fields.min')"
                            :error="!!validation.min_stay"
                            :error-messages="!!validation.min_stay ? validation.min_stay : []"
                            hide-details
                            single-line
                          ></v-text-field>
                        </td>
                        <td>
                          <v-text-field
                            v-model="roomType.max_stay"
                            dense
                            :placeholder="$lang('rate-calendar.fields.max')"
                            :error="!!validation.max_stay"
                            :error-messages="!!validation.max_stay ? validation.max_stay : []"
                            hide-details
                            single-line
                          ></v-text-field>
                        </td>
                        <td>
                          <v-checkbox
                            v-model="roomType.cta"
                            :true-value="1"
                            :false-value="0"
                            color="red"
                            hide-details
                            indeterminate
                            dense
                            on-icon="mdi-login"
                            off-icon="mdi-login"
                          />
                        </td>
                        <td>
                          <v-checkbox
                            v-model="roomType.ctd"
                            :true-value="1"
                            :false-value="0"
                            color="red"
                            hide-details
                            indeterminate
                            dense
                            on-icon="mdi-logout"
                            off-icon="mdi-logout"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-card-text>
    <v-card-text class="d-flex flex-row align-center white pb-1">
      <v-switch
        dense
        v-model="filters.withRoomTypeNames"
        label="Nomi tariffe"
        class="mr-2"
      />
      <v-text-field
        v-lazy-input:debounce="700"
        v-model="filters.search"
        prepend-inner-icon="mdi-magnify"
        :label="$lang('main.words.search')"
        hide-details
        clearable
        dense
      />
      <v-select
        dense
        :items="$in('ConstHasOptionFilters')"
        label="Derivata"
        v-model="filters.is_derived"
        hide-details
        class="mx-2"
        clearable
      />
      <v-select
        dense
        :items="$in('ConstBoolean')"
        label="Master"
        v-model="filters.is_master"
        hide-details
        class="mx-2"
        clearable
      />
      <v-select
        dense
        :items="$in('ConstBoolean')"
        label="Genitore"
        v-model="filters.is_parent"
        hide-details
        class="mx-2"
        clearable
      />
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="success" @click="submit()" :loading="loading" :disabled="loading">
        {{ $lang("main.crud.save") }}
      </v-btn>
      <v-btn color="error" @click="cancel()">
        {{ $lang("main.crud.cancel") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import MixpanelEvents from "@/constants/events";

import {
  CREATE_CALENDAR_RATE_BULK,
  CREATE_CALENDAR_RATE_BULK_RESET
} from "@/store/calendar/actions";

import {
  GET_ROOM_TYPES_COLLECTION
} from "@/store/room-types/actions";

import formable from "@/mixins/formable";
import dataFilter from "./mixins/data-filter";

export default {
  name: "CreationBulkForm",
  mixins: [
    formable,
    dataFilter
  ],
  data() {
    return {
      form: {
        end_date: null,
        start_date: null,
        weekdays: [0,1,2,3,4,5,6],
        auto_availability: null,
      },
      filters: {
        is_master: 1,
        is_parent: null,
        is_derived: 2,
        withRoomTypeNames: false,
      },
      roomTypes: [],
    }
  },
  beforeMount() {
    this.getRoomTypes();
  },
  methods: {
    getRoomTypes() {
      let extras = {};

      if (!this.filters.withRoomTypeNames) {
        extras = {
          relations: {
              unit_category: {
                selects: [
                  'id',
                  'name'
                ]
              },
              rate_plan: {
                selects: [
                  'id',
                  'name'
                ]
              }
            }
        };
      }

      this.$store.dispatch(GET_ROOM_TYPES_COLLECTION, {
        property_ids: this.propertyIds,
        ...this.filters,
        sortBy: this.filters.withRoomTypeNames ? 'name' : 'bulk',
        order: 'asc',
          _data: {
            selects: [
              'id',
              'unit_category_id',
              'rate_plan_id',
              'name',
              'min_price',
              'guests'
            ],
            ...extras
          }
      })
    },
    resetAction() {
      return CREATE_CALENDAR_RATE_BULK_RESET;
    },
    submitAction() {
      return CREATE_CALENDAR_RATE_BULK;
    },
    submitActionOptions() {
      let data = [];

      this.roomTypes.forEach((item) => {
        if (
          item.stop_sell === null &&
          item.amount === null &&
          item.availability === null &&
          item.auto_availability === null &&
          item.min_stay === null &&
          item.max_stay === null &&
          item.cta === null &&
          item.ctd === null
        ) {
          return;
        }

        let newItem = {
          ...this.form,
          ...item,
        }

        data.push(this.clean(newItem));
      });

      return data;
    },
    track() {
      this.$monitorService.track(MixpanelEvents.RATES_BULK_UPDATED);
    },
    clean(obj) {
      for (const propName in obj) {
        if (obj[propName] === null || obj[propName] === undefined) {
          delete obj[propName];
        }
      }
      return obj;
    },
    applyAll(value) {
      this.roomTypes.map((item) => {
        return item[value] = this.form[value];
      });
    },
  },
  computed: {
    initialized() {
      return !this.roomTypesRequest.loading;
    },
    ...mapState({
      loading: state => state.calendar.creationBulkRequest.loading,
      validation: state => state.calendar.creationBulkRequest.validation,
      propertyIds: state => state.settings.property.ids,
      roomTypesRequest: state => state.roomTypes.getCollectionRequest,
    }),
  },
  watch: {
    roomTypesRequest() {
      if (!this.roomTypesRequest.collection.length) {
        return;
      }

      this.roomTypes = this.roomTypesRequest.collection.map((item) => {
        let name = this.filters.withRoomTypeNames ? item.name : `${item.unit_category.name} / ${item.rate_plan.name}`;

        return {
          room_type_id: item.id,
          name: name,
          guests: item.guests,
          stop_sell: null,
          availability: null,
          auto_availability: null,
          amount: null,
          price_variation: null,
          min_stay: null,
          max_stay: null,
          cta: null,
          ctd: null,
        }
      });
    },
    'form.auto_availability'(val) {
      this.applyAll('auto_availability');
    },
    filters: {
      handler() {
        this.getRoomTypes();
      },
      deep: true
    },
  }
};
</script>
